/**
 * Admin Command Dashboard — "Executive" View
 *
 * High-density analytical dashboard for ADMIN / SUPER_ADMIN.
 * KPI strip, monthly target progress bar, branch performance grid
 * grouped by brand, "Open Map" button. NO motivational rings.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { User } from '../../domain/entities';
import { cn } from '../../ui/utils';
import { useToast } from '../../ui/Toast';
import { isRemoteMode } from '../../config/repository';
import { apiFetch } from '../../data/ApiModuleRepository';
import {
  MapPin, Plus, Navigation, Target, DollarSign, TrendingUp, TrendingDown,
  AlertTriangle, CheckCircle2, RefreshCw, Building2, Loader2,
  Check, X, Clock, History,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// ═══════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════

interface MapLocation {
  id: string;
  org_id: string;
  brand_id: string;
  branch_id: string;
  name: string;
  type: 'INTERNAL' | 'COMPETITOR';
  lat: number;
  lon: number;
  daily_target: number;
  monthly_target: number;
  current_sales?: number;
  brand_name?: string;
  brand_logo_url?: string;
  branch_name?: string;
  branch_code?: string;
  effective_daily_target?: number;
  effective_monthly_target?: number;
}

// ═══════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════

function getEffectiveTarget(loc: MapLocation): number {
  return loc.effective_daily_target ?? loc.daily_target ?? 0;
}

function getSalesRatio(loc: MapLocation): number {
  const t = getEffectiveTarget(loc);
  return t > 0 ? (loc.current_sales ?? 0) / t : 0;
}

function getPerformanceColor(ratio: number): string {
  if (ratio >= 0.8) return '#10b981';
  if (ratio >= 0.4) return '#f59e0b';
  return '#ef4444';
}

function getPerformanceLabel(ratio: number): string {
  if (ratio >= 1) return 'On Target';
  if (ratio >= 0.8) return 'Near Target';
  if (ratio >= 0.4) return 'Below Target';
  return 'Critical';
}

function formatCurrency(n: number): string {
  return n.toLocaleString('en-QA', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function getMediaUrl(url?: string): string | undefined {
  if (!url) return undefined;
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  const API_BASE = (import.meta as any).env?.VITE_API_URL ?? '';
  const token = localStorage.getItem('dm_jwt');
  const q = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${API_BASE}${url}${q}`;
}

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════

export function AdminCommandDashboard({ user }: { user: User | null }) {
  const toast = useToast();
  const nav = useNavigate();
  const [locations, setLocations] = useState<MapLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [salesInputs, setSalesInputs] = useState<Record<string, string>>({});
  const [savingSales, setSavingSales] = useState<string | null>(null);
  const [monthlyTotals, setMonthlyTotals] = useState<Record<string, { total: number; count: number }>>({});
  const [recentEntries, setRecentEntries] = useState<any[]>([]);

  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const dayOfMonth = now.getDate();

  const loadData = useCallback(async () => {
    if (!isRemoteMode()) { setLoading(false); return; }
    setError(false);
    try {
      const [sumRes, monthRes] = await Promise.all([
        apiFetch('/api/map/sales/summary'),
        apiFetch(`/api/map/sales/monthly-summary?month=${currentMonth}`),
      ]);
      setLocations((sumRes as any).data ?? []);
      const md = (monthRes as any).data ?? {};
      const tMap: Record<string, { total: number; count: number }> = {};
      for (const t of (md.totals ?? [])) {
        tMap[t.location_id] = { total: Number(t.total_sales ?? 0), count: Number(t.entry_count ?? 0) };
      }
      setMonthlyTotals(tMap);
      setRecentEntries(md.recent ?? []);
    } catch { setError(true); }
    finally { setLoading(false); }
  }, [currentMonth]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSalesSubmit = async (locId: string) => {
    const val = parseFloat(salesInputs[locId] || '0');
    if (!val || val <= 0) { toast.warning('Invalid', 'Enter a valid sales amount.'); return; }
    setSavingSales(locId);
    try {
      await apiFetch('/api/map/sales', { method: 'POST', body: JSON.stringify({ locationId: locId, salesAmount: val }) });
      toast.success('Sales Saved', 'Dashboard updated.');
      setSalesInputs(prev => ({ ...prev, [locId]: '' }));
      loadData();
    } catch (e: any) { toast.error('Failed', e.message || 'Save failed.'); }
    finally { setSavingSales(null); }
  };

  const internal = locations.filter(l => l.type === 'INTERNAL');
  const competitors = locations.filter(l => l.type === 'COMPETITOR');

  // Aggregate KPIs
  const totalSales = internal.reduce((s, l) => s + (l.current_sales ?? 0), 0);
  const totalTarget = internal.reduce((s, l) => s + getEffectiveTarget(l), 0);
  const overallRatio = totalTarget > 0 ? totalSales / totalTarget : 0;
  const onTarget = internal.filter(l => getSalesRatio(l) >= 0.8).length;
  const belowTarget = internal.filter(l => getSalesRatio(l) < 0.8 && getSalesRatio(l) > 0).length;
  const noData = internal.filter(l => (l.current_sales ?? 0) === 0).length;

  const totalMonthlySales = internal.reduce((s, l) => s + (monthlyTotals[l.id]?.total ?? 0), 0);
  const totalMonthlyTarget = internal.reduce((s, l) => s + (l.effective_monthly_target || l.monthly_target || 0), 0);
  const monthlyRemaining = Math.max(0, totalMonthlyTarget - totalMonthlySales);
  const monthlyRatio = totalMonthlyTarget > 0 ? totalMonthlySales / totalMonthlyTarget : 0;
  const dailyNeeded = daysInMonth - dayOfMonth > 0 ? Math.round(monthlyRemaining / (daysInMonth - dayOfMonth)) : 0;
  const overallRunRate = dayOfMonth > 0 ? Math.round((totalMonthlySales / dayOfMonth) * daysInMonth) : 0;
  const overallRunRateGap = overallRunRate - totalMonthlyTarget;

  // Group branches by brand
  const brandGroups = internal.reduce<Record<string, { brandName: string; brandLogo?: string; branches: MapLocation[] }>>((acc, loc) => {
    const key = loc.brand_id || '_none';
    if (!acc[key]) acc[key] = { brandName: loc.brand_name || 'Unknown Brand', brandLogo: getMediaUrl(loc.brand_logo_url), branches: [] };
    acc[key].branches.push(loc);
    return acc;
  }, {});

  // ─── Loading / Error / Empty ───

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <Loader2 size={28} className="animate-spin text-indigo-400" />
        <p className="text-sm text-slate-400 font-medium">Loading market data…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <AlertTriangle size={28} className="text-amber-400" />
        <p className="text-sm text-slate-500 font-medium">Failed to load data.</p>
        <button onClick={() => { setLoading(true); loadData(); }}
          className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-700 transition-colors">
          <RefreshCw size={13} /> Retry
        </button>
      </div>
    );
  }

  if (locations.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
        <MapPin size={32} className="mx-auto text-slate-300 mb-3" />
        <p className="text-sm font-semibold text-slate-500">No locations configured yet.</p>
        <p className="text-xs text-slate-400 mt-1">Go to Location Manager to add your branches and competitors.</p>
        <button onClick={() => nav('/apps/market-map/location-manager')}
          className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-700 transition-colors">
          <Plus size={12} className="inline mr-1" /> Add Locations
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Market Intelligence</h2>
        <p className="text-sm text-slate-500 mt-1">Command center — {internal.length} branches, {competitors.length} competitors.</p>
      </div>

      {/* ─── KPI Strip ─── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: <Building2 size={18} className="text-indigo-600" />, bg: 'bg-indigo-100', label: 'Branches', value: internal.length, sub: `${competitors.length} competitors` },
          { icon: <DollarSign size={18} className="text-emerald-600" />, bg: 'bg-emerald-100', label: "Today's Sales", value: formatCurrency(totalSales), sub: `Target: ${formatCurrency(totalTarget)}` },
          { icon: <CheckCircle2 size={18} className="text-emerald-600" />, bg: 'bg-emerald-100', label: 'On Target', value: onTarget, sub: `${belowTarget} below · ${noData} no data`, valueColor: 'text-emerald-600' },
          { icon: overallRatio >= 0.8 ? <TrendingUp size={18} className="text-emerald-600" /> : <TrendingDown size={18} className="text-red-600" />, bg: overallRatio >= 0.8 ? 'bg-emerald-100' : 'bg-red-100', label: 'Performance', value: `${(overallRatio * 100).toFixed(0)}%`, sub: getPerformanceLabel(overallRatio), valueColor: overallRatio >= 0.8 ? 'text-emerald-600' : 'text-red-600' },
        ].map((kpi, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', kpi.bg)}>{kpi.icon}</div>
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">{kpi.label}</span>
            </div>
            <p className={cn('text-3xl font-black', (kpi as any).valueColor || 'text-slate-900')}>{kpi.value}</p>
            <p className="text-[10px] text-slate-400 mt-1">{kpi.sub}</p>
          </div>
        ))}
      </div>

      {/* ─── Monthly Target Progress ─── */}
      {totalMonthlyTarget > 0 && (
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-bold text-slate-300 uppercase tracking-wider">Monthly Target Progress</h3>
              <p className="text-[10px] text-slate-400 mt-0.5">
                {new Date(now.getFullYear(), now.getMonth()).toLocaleString('en', { month: 'long', year: 'numeric' })} · Day {dayOfMonth} of {daysInMonth}
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-black">{(monthlyRatio * 100).toFixed(1)}%</p>
              <p className="text-[10px] text-slate-400">achieved</p>
            </div>
          </div>
          <div className="relative h-4 bg-slate-700 rounded-full overflow-hidden mb-4">
            <div className="h-full rounded-full transition-all duration-700"
              style={{
                width: `${Math.min(monthlyRatio * 100, 100)}%`,
                background: monthlyRatio >= 0.8 ? 'linear-gradient(90deg, #10b981, #34d399)' : monthlyRatio >= 0.5 ? 'linear-gradient(90deg, #f59e0b, #fbbf24)' : 'linear-gradient(90deg, #ef4444, #f87171)'
              }} />
            <div className="absolute top-0 h-full w-0.5 bg-white/40" style={{ left: `${(dayOfMonth / daysInMonth) * 100}%` }} />
          </div>
          <div className="grid grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-lg font-black text-emerald-400">{formatCurrency(totalMonthlySales)}</p>
              <p className="text-[10px] text-slate-400">Achieved</p>
            </div>
            <div>
              <p className="text-lg font-black text-amber-400">{formatCurrency(monthlyRemaining)}</p>
              <p className="text-[10px] text-slate-400">Remaining</p>
            </div>
            <div>
              <p className="text-lg font-black text-sky-400">{formatCurrency(dailyNeeded)}</p>
              <p className="text-[10px] text-slate-400">Needed/Day</p>
            </div>
            <div>
              <p className={cn('text-lg font-black', overallRunRateGap >= 0 ? 'text-emerald-400' : 'text-red-400')}>{formatCurrency(overallRunRate)}</p>
              <p className="text-[10px] text-slate-400">Run Rate ({overallRunRateGap >= 0 ? '+' : ''}{formatCurrency(overallRunRateGap)})</p>
            </div>
          </div>
        </div>
      )}

      {/* ─── Branch Performance Grid — Grouped by Brand ─── */}
      {internal.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-slate-900">Branch Performance — Today</h3>
            <div className="flex items-center gap-2">
              <button onClick={() => nav('/apps/market-map/live-map')}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-lg text-xs font-bold hover:bg-indigo-100 transition-colors">
                <Navigation size={12} /> Open Map
              </button>
            </div>
          </div>

          {/* Brand groups */}
          {Object.entries(brandGroups).map(([brandId, group]) => (
            <div key={brandId} className="mb-5">
              {/* Brand header */}
              {Object.keys(brandGroups).length > 1 && (
                <div className="flex items-center gap-2.5 mb-3">
                  {group.brandLogo ? (
                    <img src={group.brandLogo} alt="" className="w-6 h-6 rounded-lg object-cover" />
                  ) : (
                    <div className="w-6 h-6 rounded-lg bg-indigo-50 flex items-center justify-center">
                      <Building2 size={12} className="text-indigo-400" />
                    </div>
                  )}
                  <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">{group.brandName}</span>
                  <span className="text-[10px] text-slate-400">{group.branches.length} branches</span>
                </div>
              )}

              {/* Compact branch rows */}
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                {group.branches
                  .sort((a, b) => getSalesRatio(b) - getSalesRatio(a))
                  .map((loc, idx) => {
                    const ratio = getSalesRatio(loc);
                    const color = getPerformanceColor(ratio);
                    const target = getEffectiveTarget(loc);
                    const mTotal = monthlyTotals[loc.id]?.total ?? 0;
                    const mTarget = loc.effective_monthly_target || loc.monthly_target || 0;
                    const mRatio = mTarget > 0 ? mTotal / mTarget : 0;
                    const mRemaining = Math.max(0, mTarget - mTotal);
                    const daysLeft = Math.max(0, daysInMonth - dayOfMonth);
                    const dailyReq = daysLeft > 0 ? Math.round(mRemaining / daysLeft) : 0;
                    const runR = dayOfMonth > 0 ? Math.round((mTotal / dayOfMonth) * daysInMonth) : 0;
                    const runGap = runR - mTarget;

                    return (
                      <div key={loc.id} className={cn('px-5 py-3.5 flex items-center gap-4 hover:bg-slate-50/50 transition-colors', idx > 0 && 'border-t border-slate-100')}>
                        {/* Status dot + Name */}
                        <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-bold text-slate-800 truncate">{loc.name}</p>
                            {loc.branch_code && <span className="text-[9px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded font-mono shrink-0">{loc.branch_code}</span>}
                          </div>
                          <div className="flex items-center gap-3 mt-0.5">
                            <span className="text-[10px] text-slate-400">
                              Today: <span className="font-bold" style={{ color }}>{formatCurrency(loc.current_sales ?? 0)}</span> / {formatCurrency(target)}
                            </span>
                            {mTarget > 0 && (
                              <>
                                <span className="text-[10px] text-slate-300">·</span>
                                <span className="text-[10px] text-slate-400">
                                  MTD: <span className="font-bold text-slate-600">{formatCurrency(mTotal)}</span> / {formatCurrency(mTarget)}
                                  <span className="ml-1 font-bold" style={{ color: mRatio >= 0.8 ? '#10b981' : mRatio >= 0.5 ? '#f59e0b' : '#ef4444' }}>
                                    ({(mRatio * 100).toFixed(0)}%)
                                  </span>
                                </span>
                                <span className="text-[10px] text-slate-300">·</span>
                                <span className="text-[10px] text-slate-400">
                                  Need: <span className="font-bold text-indigo-500">{formatCurrency(dailyReq)}</span>
                                </span>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Performance badge */}
                        <span className="inline-flex px-2 py-0.5 rounded-full text-[9px] font-bold shrink-0"
                          style={{ backgroundColor: `${color}15`, color }}>
                          {getPerformanceLabel(ratio)}
                        </span>

                        {/* Today bar + Progress */}
                        <div className="w-24 shrink-0">
                          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(ratio * 100, 100)}%`, backgroundColor: color }} />
                          </div>
                        </div>

                        {/* Compact sales entry */}
                        <div className="flex items-center gap-1.5 shrink-0">
                          <div className="relative">
                            <DollarSign size={10} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-300" />
                            <input type="number" placeholder="Sales…" step="0.01"
                              value={salesInputs[loc.id] || ''}
                              onChange={e => setSalesInputs(prev => ({ ...prev, [loc.id]: e.target.value }))}
                              onKeyDown={e => { if (e.key === 'Enter') handleSalesSubmit(loc.id); }}
                              className="w-24 pl-5 pr-2 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:border-indigo-300 focus:ring-1 focus:ring-indigo-100 outline-none" />
                          </div>
                          <button onClick={() => handleSalesSubmit(loc.id)} disabled={savingSales === loc.id}
                            className="p-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50">
                            {savingSales === loc.id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                          </button>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          ))}
        </div>
      )}


    </div>
  );
}
